// src/components/auction/auctionAnalysis.js
// Motore di analisi asta: ranking rose, individuazione lacune e motore scambi.
// Puramente funzionale/pure: non tocca Firestore, lavora sui dati già caricati
// (oltre a buildGroupTrendSeries, che lavora su player doc già recuperati altrove).
import { calcPlayerScore, v, ROLE_ORDER, FORMATIONS, getAiPerformanceScore, getAiRoleFamily } from './auctionCommon';
import { getPrimaryRoleValue, splitRoleTokens } from './auctionRosterUtils';
import { MANTRA_PRESETS } from './MantraRoleConfiguration';
import { buildSeasonTrendSeries } from '../../firebase/playerService';
import { getUpcomingCalendarDifficulty } from '../../data/serieACalendar';

// Righe "T" (trequarti) contano come reparto centrocampo/movimento nel bucket
// finanziario già usato ovunque nell'app (getMantraBucket: E/C/M/W/T -> 'C').
function getMantraStartersFromPreset(presetKey) {
  const preset = MANTRA_PRESETS[presetKey];
  if (!preset) return null;

  const counts = { P: 0, D: 0, C: 0, A: 0 };
  preset.rows.forEach((row) => {
    const bucket = row.key === 'T' ? 'C' : row.key;
    if (counts[bucket] !== undefined) counts[bucket] += (row.slots || []).length;
  });

  return counts;
}

// Riferimento "titolari per ruolo" usato per individuare le lacune di formazione
// e per capire quanti titolari servono davvero in un ruolo durante gli scambi.
// In Mantra usa il modulo effettivo scelto in asta (MANTRA_PRESETS); in Classic
// usa il modulo classico (FORMATIONS), con fallback ragionevoli in entrambi i casi.
function getStartersReference(auction, rolesMode = 'classic') {
  if (rolesMode === 'mantra') {
    const presetKey = String(auction?.formationPreset || '343').trim();
    return getMantraStartersFromPreset(presetKey) || getMantraStartersFromPreset('343');
  }

  const preset = String(auction?.formationPreset || '4-3-3').trim();
  return FORMATIONS[preset] || FORMATIONS['4-3-3'];
}

function getPurchaseRoleValue(purchase) {
  return purchase?.playerSnapshot?.role ?? purchase?.role ?? 'A';
}

function getPurchaseBucket(purchase, rolesMode) {
  return getPrimaryRoleValue(getPurchaseRoleValue(purchase), rolesMode) || 'A';
}

function emptyRoleMap(fill = () => []) {
  return { P: fill(), D: fill(), C: fill(), A: fill() };
}

// Punteggio "di rosa" per la classifica: a differenza di calcPlayerScore (più
// orientato al valore di mercato, usato per giudicare la correttezza di uno
// scambio), qui pesa statistiche stagione corrente + precedente, presenze
// giocate (gestendo con garbo l'inizio campionato) e realizzazione offensiva.
function getRosterQualityScore(purchase, rolesMode) {
  const roleFamily = getAiRoleFamily(purchase, rolesMode);
  return getAiPerformanceScore(purchase, roleFamily);
}

function getPurchaseCurrentCaps(purchase) {
  return v(purchase?.playerSnapshot?.caps ?? purchase?.caps ?? 0);
}

function getPurchasePreviousCaps(purchase) {
  return v(purchase?.playerSnapshot?.previousSeasonStats?.caps ?? purchase?.previousSeasonStats?.caps ?? 0);
}

function getPurchaseGoals(purchase) {
  return v(purchase?.playerSnapshot?.goals ?? purchase?.goals ?? 0);
}

function getPurchaseAssists(purchase) {
  return v(purchase?.playerSnapshot?.assists ?? purchase?.assists ?? 0);
}

// ── Statistiche/ranking rose ────────────────────────────────────────────────

export function buildTeamRosterStats({
  teamNames = [],
  rosterByTeam = {},
  teamEconomy = {},
  rolesMode = 'classic',
  auction = null,
}) {
  const starters = getStartersReference(auction, rolesMode);
  const totalStartersNeeded = ROLE_ORDER.reduce((acc, role) => acc + (starters[role] || 0), 0);

  const perTeam = teamNames.map((team) => {
    const roster = (rosterByTeam[team] || []).filter((p) => !p?.deleted);
    const byRole = emptyRoleMap();

    roster.forEach((purchase) => {
      const bucket = getPurchaseBucket(purchase, rolesMode);
      (byRole[bucket] || (byRole[bucket] = [])).push(purchase);
    });

    const roleBreakdown = ROLE_ORDER.map((role) => {
      const list = byRole[role] || [];
      const roleScore = list.reduce((acc, p) => acc + getRosterQualityScore(p, rolesMode), 0);
      const roleSpent = list.reduce((acc, p) => acc + v(p.credits), 0);

      return {
        role,
        owned: list.length,
        needed: starters[role] || 0,
        deficit: Math.max((starters[role] || 0) - list.length, 0),
        totalScore: roleScore,
        avgScore: list.length ? roleScore / list.length : 0,
        spent: roleSpent,
        players: list,
      };
    });

    const totalScore = roleBreakdown.reduce((acc, r) => acc + r.totalScore, 0);
    const totalOwned = roster.length;
    const avgScore = totalOwned ? totalScore / totalOwned : 0;
    const startersOwned = ROLE_ORDER.reduce(
      (acc, role) => acc + Math.min(byRole[role]?.length || 0, starters[role] || 0),
      0
    );
    const completeness = totalStartersNeeded > 0 ? Math.min(startersOwned / totalStartersNeeded, 1) : 0;
    const economy = teamEconomy[team] || {};
    const spent = v(economy.spent);
    const efficiency = spent > 0 ? totalScore / spent : totalScore > 0 ? totalScore : 0;

    const currentCaps = roster.reduce((acc, p) => acc + getPurchaseCurrentCaps(p), 0);
    const previousCaps = roster.reduce((acc, p) => acc + getPurchasePreviousCaps(p), 0);
    const goals = roster.reduce((acc, p) => acc + getPurchaseGoals(p), 0);
    const assists = roster.reduce((acc, p) => acc + getPurchaseAssists(p), 0);

    const topSpendPlayer = roster.length
      ? [...roster].sort((a, b) => v(b.credits) - v(a.credits))[0]
      : null;

    return {
      team,
      roster,
      byRole,
      roleBreakdown,
      totalScore,
      avgScore,
      totalOwned,
      completeness,
      spent,
      remaining: v(economy.remaining),
      efficiency,
      currentCaps,
      previousCaps,
      avgCurrentCaps: totalOwned ? currentCaps / totalOwned : 0,
      goals,
      assists,
      topSpendPlayer,
      // powerScore: premia chi ha costruito una rosa forte, penalizza leggermente chi
      // è ancora molto scoperto (una rosa a metà non può essere "la migliore").
      powerScore: totalScore * (0.6 + 0.4 * completeness),
    };
  });

  return perTeam;
}

// ── Dettaglio squadra (overlay griglia F1) ──────────────────────────────────

function getEffectiveMarkavg(purchase) {
  const snap = purchase?.playerSnapshot || {};
  return v(snap.caps) > 0 ? v(snap.markavg) : v(snap.previousSeasonStats?.markavg);
}

function getEffectiveFmarkavg(purchase) {
  const snap = purchase?.playerSnapshot || {};
  return v(snap.caps) > 0 ? v(snap.fmarkavg) : v(snap.previousSeasonStats?.fmarkavg);
}

function getOffensivePotential(purchase) {
  const snap = purchase?.playerSnapshot || {};
  const goals = v(snap.goals) + v(snap.previousSeasonStats?.goals) * 0.5;
  const assists = v(snap.assists) + v(snap.previousSeasonStats?.assists) * 0.5;
  return goals * 3 + assists * 2 + getEffectiveFmarkavg(purchase) * 1.5;
}

// Costruisce il dettaglio "da overlay" per una squadra: MV/FV previste, giocatore
// di punta per spesa, un'eventuale scommessa low-cost e la FV prevista per ruolo.
export function buildTeamInsightDetail(teamStat) {
  const roster = teamStat?.roster || [];
  if (!roster.length) return null;

  const predictedMv = roster.reduce((acc, p) => acc + getEffectiveMarkavg(p), 0) / roster.length;
  const predictedFv = roster.reduce((acc, p) => acc + getEffectiveFmarkavg(p), 0) / roster.length;

  const predictedFvByRole = teamStat.roleBreakdown.map((r) => ({
    role: r.role,
    owned: r.owned,
    predictedFv: r.players.length
      ? r.players.reduce((acc, p) => acc + getEffectiveFmarkavg(p), 0) / r.players.length
      : 0,
  }));

  const bargainCandidates = roster.filter((p) => v(p.credits) <= 1);
  const bargainPlayer = bargainCandidates.length
    ? [...bargainCandidates].sort((a, b) => getOffensivePotential(b) - getOffensivePotential(a))[0]
    : null;

  return {
    predictedMv,
    predictedFv,
    predictedFvByRole,
    topSpendPlayer: teamStat.topSpendPlayer || null,
    bargainPlayer,
    powerScore: teamStat.powerScore,
  };
}

export function rankTeams(teamStats = []) {
  return [...teamStats].sort((a, b) => b.powerScore - a.powerScore);
}

// Rileva se il campionato è ancora "giovane" (poche presenze medie a livello
// di lega): usato per mostrare una nota di trasparenza sul ranking, dato che
// il punteggio già integra la stagione precedente in questi casi.
export function isEarlySeasonSample(teamStats = [], threshold = 4) {
  const withRoster = teamStats.filter((t) => t.totalOwned > 0);
  if (!withRoster.length) return false;
  const leagueAvgCaps = withRoster.reduce((acc, t) => acc + t.avgCurrentCaps, 0) / withRoster.length;
  return leagueAvgCaps < threshold;
}

// ── Lacune per ruolo ─────────────────────────────────────────────────────────

const WEAKNESS_SEVERITY = { high: 3, medium: 2, low: 1 };

export function detectTeamWeaknesses(teamStats = []) {
  const roleLeagueAvg = { P: [], D: [], C: [], A: [] };

  teamStats.forEach((team) => {
    team.roleBreakdown.forEach((r) => {
      if (r.owned > 0) roleLeagueAvg[r.role].push(r.avgScore);
    });
  });

  const leagueAvgByRole = {};
  ROLE_ORDER.forEach((role) => {
    const values = roleLeagueAvg[role];
    leagueAvgByRole[role] = values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  });

  return teamStats.map((team) => {
    const weaknesses = [];

    team.roleBreakdown.forEach((r) => {
      if (r.owned === 0 && r.needed > 0) {
        weaknesses.push({
          role: r.role,
          severity: 'high',
          label: 'Ruolo scoperto',
          detail: `Nessun giocatore acquistato: servono almeno ${r.needed} titolari.`,
        });
        return;
      }

      if (r.deficit > 0) {
        weaknesses.push({
          role: r.role,
          severity: r.deficit >= 2 ? 'high' : 'medium',
          label: 'Rosa incompleta',
          detail: `${r.owned}/${r.needed} titolari necessari per schierare la formazione.`,
        });
      }

      const leagueAvg = leagueAvgByRole[r.role];
      if (r.owned > 0 && leagueAvg > 0) {
        const ratio = r.avgScore / leagueAvg;
        if (ratio < 0.7) {
          weaknesses.push({
            role: r.role,
            severity: 'high',
            label: 'Qualità sotto la media',
            detail: `Rendimento medio del ruolo ${Math.round((1 - ratio) * 100)}% sotto la media di lega.`,
          });
        } else if (ratio < 0.85) {
          weaknesses.push({
            role: r.role,
            severity: 'medium',
            label: 'Qualità sotto la media',
            detail: `Rendimento medio del ruolo ${Math.round((1 - ratio) * 100)}% sotto la media di lega.`,
          });
        }
      }
    });

    weaknesses.sort((a, b) => (WEAKNESS_SEVERITY[b.severity] || 0) - (WEAKNESS_SEVERITY[a.severity] || 0));

    return { team: team.team, weaknesses };
  });
}

// ── Indice svincolati per scarsità di mercato ───────────────────────────────

export function buildFreeAgentIndex({ players = [], purchases = [], rolesMode = 'classic' }) {
  const ownedIds = new Set(
    (purchases || [])
      .filter((p) => !p?.deleted)
      .map((p) => String(p.playerId))
  );

  const byRole = emptyRoleMap();

  (players || []).forEach((player) => {
    if (ownedIds.has(String(player.id))) return;
    const bucket = getPrimaryRoleValue(player.role, rolesMode) || 'A';
    (byRole[bucket] || (byRole[bucket] = [])).push(player);
  });

  return byRole;
}

// ── Utilità condivise ────────────────────────────────────────────────────────

function sumScore(list = []) {
  return list.reduce((acc, p) => acc + calcPlayerScore(p), 0);
}

function sumCredits(list = []) {
  return list.reduce((acc, p) => acc + v(p.credits), 0);
}

function countByBucket(list = [], rolesMode) {
  const counts = { P: 0, D: 0, C: 0, A: 0 };
  list.forEach((p) => {
    const bucket = getPurchaseBucket(p, rolesMode);
    counts[bucket] = (counts[bucket] || 0) + 1;
  });
  return counts;
}

function combinations(arr, size) {
  if (size === 0) return [[]];
  if (arr.length < size) return [];
  const [first, ...rest] = arr;
  const withFirst = combinations(rest, size - 1).map((c) => [first, ...c]);
  const withoutFirst = combinations(rest, size);
  return [...withFirst, ...withoutFirst];
}

// ── Suggerimenti di scambio combinati: "scambio i giocatori selezionati (tutti
// insieme, con un solo partecipante) per rinforzarmi in un ruolo specifico" ──
//
// Il pacchetto ricevuto rispecchia SEMPRE la composizione di ruoli di quello
// offerto (stesso numero di giocatori per ciascun ruolo), perché ogni ruolo ha
// un limite di rosa che non si può superare. Il ruolo "preferito" (scelto
// dall'utente quando la selezione copre più ruoli) non cambia questa struttura:
// serve solo a scegliere, a parità di ruoli, il candidato che massimizza il
// guadagno proprio in quel reparto.

const GROUP_SUGGESTION_POOL_SIZE = 10;
// Alzato rispetto al minimo necessario (bastano MAX_PER_TEAM=3 pacchetti):
// più opzioni "grezze" sopravvivono ai filtri di realismo, così pickDiverseCombos
// ha margine per scegliere 3 pacchetti senza giocatori in comune, invece di
// ripescare sempre le stesse 2-3 combinazioni col punteggio combinato più alto.
const GROUP_SUGGESTION_MAX_OPTIONS_PER_BUCKET = 12;
const GROUP_SUGGESTION_MAX_PER_TEAM = 3;
const GROUP_SUGGESTION_MAX_TEAMS = 5;

// Un vero fantallenatore rifiuta uno scambio troppo sbilanciato in tuo favore:
// il pacchetto che ricevi non può valere più di questa percentuale in più di
// quello che offri, altrimenti il suggerimento non è realistico. Tenuto basso
// apposta: un guadagno modesto è credibile, un "regalo" no.
const GROUP_SUGGESTION_MAX_GAIN_RATIO = 0.12;

// Quando la selezione copre PIÙ ruoli e scegli quello da rinforzare, lo
// scambio deve raccontare un vero trade-off, non un pareggio travestito:
// nel ruolo scelto devi guadagnare per davvero (almeno questa percentuale),
// altrimenti la scelta del ruolo sarebbe ininfluente...
const GROUP_SUGGESTION_MULTI_TARGET_MIN_GAIN_RATIO = 0.02;

// ...e negli altri ruoli coinvolti devi perderci per davvero (tra questi due
// estremi): è il "prezzo" che paghi per finanziare il rinforzo nel ruolo
// scelto. Senza un indebolimento minimo il pacchetto sembrerebbe un regalo
// anche nel ruolo secondario; senza un tetto massimo sembrerebbe una svendita.
const GROUP_SUGGESTION_MULTI_SIDE_MIN_LOSS_RATIO = 0.02;
const GROUP_SUGGESTION_MULTI_SIDE_MAX_LOSS_RATIO = 0.18;

export function getDistinctBuckets(selectedPurchases = [], rolesMode = 'classic') {
  const buckets = new Set(selectedPurchases.map((p) => getPurchaseBucket(p, rolesMode)));
  return [...buckets];
}

// ── Copertura dei reparti ("non rischiare di giocare in 10") ────────────────
// Quanto una rosa è in grado di schierare un 11 completo + avere riserve vere.
// Pesa molto il coprire i titolari richiesti dal modulo, poi premia (con
// rendimenti decrescenti) l'avere 1-2 alternative per reparto. `hardGap` = numero
// totale di titolari MANCANTI (con quel numero si gioca in 10/9): è la cosa da
// azzerare per prima.
//
// In CLASSIC lavora a reparto P/D/C/A. In MANTRA guarda gli SLOT reali del
// modulo scelto (es. Dc·Dc·Dc/B) e prova ad assegnare i giocatori agli slot in
// base ai loro token: è molto più preciso perché un difensore "solo Ds" non
// copre uno slot "Dc" anche se il reparto D avrebbe i numeri.

function getPlayerMantraTokens(purchase) {
  return splitRoleTokens(getPurchaseRoleValue(purchase));
}

function slotAcceptsPlayer(slotOptions, tokens) {
  return slotOptions.some((opt) => tokens.includes(opt));
}

function computeMantraCoverageScore(alive, presetKey) {
  const preset = MANTRA_PRESETS[presetKey] || MANTRA_PRESETS['343'];
  if (!preset) return null;

  const slots = preset.rows.flatMap((row) => row.slots); // 11 slot = array di token ammessi
  const players = alive.map((p) => ({
    id: String(p.playerId ?? p.id),
    tokens: getPlayerMantraTokens(p),
    score: calcPlayerScore(p),
  }));

  // Assegnazione greedy: prima gli slot più difficili da coprire (meno
  // giocatori eleggibili), poi a ciascuno il miglior giocatore libero.
  const slotInfo = slots.map((options, idx) => ({
    idx,
    options,
    eligible: players.filter((pl) => slotAcceptsPlayer(options, pl.tokens)),
  }));
  slotInfo.sort((a, b) => a.eligible.length - b.eligible.length);

  const used = new Set();
  let filled = 0;
  slotInfo.forEach((slot) => {
    const best = slot.eligible
      .filter((pl) => !used.has(pl.id))
      .sort((a, b) => b.score - a.score)[0];
    if (best) { used.add(best.id); filled += 1; }
  });

  const totalSlots = slots.length || 11;
  const hardGap = totalSlots - filled;
  // riserve "vere": chi non è titolare ma potrebbe coprire almeno uno slot.
  const usefulBench = players.filter(
    (pl) => !used.has(pl.id) && slots.some((opts) => slotAcceptsPlayer(opts, pl.tokens))
  ).length;

  const score = filled * 12 + Math.min(usefulBench, 3) * 3.5;
  return {
    score,
    hardGap,
    playableXI: hardGap === 0,
    filled,
    totalSlots,
    usefulBench,
    detail: {},
  };
}

// ── Disponibilità: infortuni + probabili formazioni ────────────────────────
// `availabilityById` è una Map o un oggetto playerId -> { injury?, lineup? }.
function availabilityGet(availabilityById, playerId) {
  if (!availabilityById) return null;
  const key = String(playerId);
  if (typeof availabilityById.get === 'function') return availabilityById.get(key) || null;
  return availabilityById[key] || null;
}

// Valuta una lista di giocatori (di solito quelli che RICEVI) dal punto di
// vista della disponibilità: quanti sono infortunati / in panchina.
// Ritorna { flags: [{playerId, name, kind, detail}], penalty } dove penalty è
// un malus (>= 0) da sottrarre a un punteggio-obiettivo.
function assessListAvailability(list = [], availabilityById) {
  const flags = [];
  let penalty = 0;
  (list || []).forEach((p) => {
    const a = availabilityGet(availabilityById, p.playerId ?? p.id);
    if (!a) return;
    const name = p?.playerSnapshot?.name || p?.playerName || p?.name || 'Un giocatore';
    if (a.injury) {
      const back = a.injury.expectedReturn ? ` — rientro previsto: ${a.injury.expectedReturn}` : '';
      flags.push({ playerId: String(p.playerId ?? p.id), name, kind: 'injury', detail: `${a.injury.reason || 'infortunato'}${back}` });
      penalty += 0.9;
    } else if (a.lineup && a.lineup.status === 'bench') {
      flags.push({ playerId: String(p.playerId ?? p.id), name, kind: 'bench', detail: 'dato in panchina nelle probabili formazioni' });
      penalty += 0.35;
    }
  });
  return { flags, penalty };
}

export function computeCoverageScore(roster = [], auction = null, rolesMode = 'classic') {
  const alive = (roster || []).filter((p) => !p?.deleted);

  if (rolesMode === 'mantra') {
    const presetKey = String(auction?.formationPreset || '343').trim();
    const mantra = computeMantraCoverageScore(alive, presetKey);
    if (mantra) return mantra;
  }

  const starters = getStartersReference(auction, rolesMode);
  const byBucket = { P: 0, D: 0, C: 0, A: 0 };
  alive.forEach((p) => {
    const b = getPurchaseBucket(p, rolesMode);
    if (byBucket[b] !== undefined) byBucket[b] += 1;
  });

  let score = 0;
  let hardGap = 0;
  const detail = {};
  ROLE_ORDER.forEach((role) => {
    const needed = starters[role] || 0;
    const have = byBucket[role] || 0;
    const missing = Math.max(0, needed - have);
    const bench = Math.max(0, have - needed);
    hardGap += missing;
    score += Math.min(have, needed) * 12;
    score += Math.min(bench, 1) * 4 + Math.min(Math.max(bench - 1, 0), 1) * 1.5;
    detail[role] = { needed, have, missing, bench };
  });

  return { score, hardGap, playableXI: hardGap === 0, detail };
}

// ── Analisi "win-win": quanto lo scambio conviene anche all'altra squadra ───
//
// Uno scambio alla pari sui numeri grezzi raramente viene accettato: quello che
// convince l'altro fantallenatore è NON peggiorare la sua formazione titolare
// (cede panchina, riceve profondità) e ricevere qualcosa di tangibile in cambio
// (valore d'asta, presenze, fantamedia). Qui simuliamo le rose post-scambio e
// trasformiamo questi segnali in una probabilità di accettazione + motivazioni.

// Somma del punteggio dei migliori "titolari" per ruolo (top-N per calcPlayerScore).
function topStartersScore(roster, starters, rolesMode) {
  const byBucket = emptyRoleMap();
  roster.forEach((p) => {
    byBucket[getPurchaseBucket(p, rolesMode)].push(calcPlayerScore(p));
  });

  return ROLE_ORDER.reduce((acc, role) => {
    const sorted = [...byBucket[role]].sort((a, b) => b - a);
    return acc + sorted.slice(0, starters[role] || 0).reduce((sum, s) => sum + s, 0);
  }, 0);
}

// Insieme degli id dei titolari di una rosa (top-N per ruolo).
function startersIdSet(roster, starters, rolesMode) {
  const byBucket = emptyRoleMap();
  roster.forEach((p) => {
    byBucket[getPurchaseBucket(p, rolesMode)].push(p);
  });

  const ids = new Set();
  ROLE_ORDER.forEach((role) => {
    [...byBucket[role]]
      .sort((a, b) => calcPlayerScore(b) - calcPlayerScore(a))
      .slice(0, starters[role] || 0)
      .forEach((p) => ids.add(String(p.playerId)));
  });
  return ids;
}

function avgEffectiveFmarkavg(list = []) {
  if (!list.length) return 0;
  return list.reduce((acc, p) => acc + getEffectiveFmarkavg(p), 0) / list.length;
}

function getPurchaseName(purchase) {
  return purchase?.playerSnapshot?.name || purchase?.playerName || 'Un giocatore';
}

// ── Utilità del pacchetto offerto per chi lo riceve ─────────────────────────
// Ogni fantallenatore schiera al massimo 11 titolari: un giocatore in arrivo ha
// valore d'uso solo se entra nell'undici, se alza il livello della panchina o
// se copre un ruolo rimasto senza riserve. Altrimenti è un doppione che non
// vedrà mai il campo — e nessuno accetta uno scambio per collezionare doppioni.
// Classifica ciascun giocatore offerto rispetto alla rosa post-scambio dell'altra
// squadra: { starterArrivals, benchUpgrades, depthFills, useless, usefulCount }.
function assessOfferedPackageUtility({ offered, theirRoster, received, starters, rolesMode }) {
  const receivedIds = new Set(received.map((p) => String(p.playerId)));
  const remaining = theirRoster.filter((p) => !receivedIds.has(String(p.playerId)));

  const remainingByBucket = emptyRoleMap();
  remaining.forEach((p) => remainingByBucket[getPurchaseBucket(p, rolesMode)].push(p));
  ROLE_ORDER.forEach((role) => {
    remainingByBucket[role].sort((a, b) => calcPlayerScore(b) - calcPlayerScore(a));
  });

  const afterByBucket = emptyRoleMap();
  [...remaining, ...offered].forEach((p) => afterByBucket[getPurchaseBucket(p, rolesMode)].push(p));
  ROLE_ORDER.forEach((role) => {
    afterByBucket[role].sort((a, b) => calcPlayerScore(b) - calcPlayerScore(a));
  });

  const starterArrivals = [];
  const benchUpgrades = [];
  const depthFills = [];
  const useless = [];

  offered.forEach((p) => {
    const bucket = getPurchaseBucket(p, rolesMode);
    const startersNeeded = starters[bucket] || 0;

    // Entra nei titolari? (posizione nella rosa post-scambio di quel ruolo)
    const rank = afterByBucket[bucket].findIndex((x) => String(x.playerId) === String(p.playerId));
    if (rank !== -1 && rank < startersNeeded) {
      starterArrivals.push(p);
      return;
    }

    // Non è titolare: vale come riserva solo se la panchina ne ha bisogno.
    const benchBefore = remainingByBucket[bucket].slice(startersNeeded);
    if (!benchBefore.length) {
      // Ruolo senza alternative: qualunque rinforzo dà profondità necessaria.
      depthFills.push(p);
      return;
    }

    const score = calcPlayerScore(p);
    const bestBenchScore = calcPlayerScore(benchBefore[0]);
    const avgBenchScore = benchBefore.reduce((acc, x) => acc + calcPlayerScore(x), 0) / benchBefore.length;

    if (score > bestBenchScore || score > avgBenchScore * 1.05) {
      benchUpgrades.push(p);
    } else {
      useless.push(p);
    }
  });

  return {
    starterArrivals,
    benchUpgrades,
    depthFills,
    useless,
    usefulCount: starterArrivals.length + benchUpgrades.length + depthFills.length,
  };
}

function sumCaps(list = []) {
  return list.reduce((acc, p) => acc + getPurchaseCurrentCaps(p), 0);
}

function getPurchaseTeamName(purchase) {
  return purchase?.playerSnapshot?.team || purchase?.playerTeam || '';
}

// Produzione pesata per ruolo (attacco > centrocampo > difesa nella probabilità
// di contribuire al gol): riusa getAiPerformanceScore, già role-aware (D/C/A
// hanno coefficienti diversi su gol/assist), invece di un punteggio uniforme
// come calcPlayerScore — qui conta "quanto rendimento offensivo/difensivo
// realistico per il ruolo" si scambia, non solo il valore di mercato.
function sumRoleAwareProduction(list = [], rolesMode) {
  return list.reduce((acc, p) => {
    const roleFamily = getAiRoleFamily(p, rolesMode);
    return acc + getAiPerformanceScore(p, roleFamily);
  }, 0);
}

// Difficoltà media del calendario (prossime 7 giornate) dei club coinvolti:
// più basso = più abbordabile. Ritorna null se nessun giocatore ha un club
// riconosciuto nel calendario (es. tutti svincolati/esteri).
function avgCalendarDifficulty(list = []) {
  const values = list
    .map((p) => getUpcomingCalendarDifficulty(getPurchaseTeamName(p), 7))
    .filter((d) => d !== null);
  return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
}

// ── Segnali "da fantallenatore esperto" ─────────────────────────────────────
// Tre letture che vanno oltre il puro valore di mercato — lo stesso modo in
// cui un fantallenatore navigato guarda un giocatore prima di uno scambio.

// Forma recente vs media stagionale: intercetta chi è "in un momento" (o in
// calo), cosa che la sola media stagionale nasconde. ratingsById: Map o
// oggetto playerId -> { ratingsHistory, ratingsMaxDay } (stessa forma già
// usata per la "Prova del nove" — fornito dal chiamante, mai recuperato qui:
// questo motore resta puro/sincrono).
const RECENT_FORM_WINDOW = 4;
export function computeRecentFormDelta(purchase, ratingsById) {
  if (!ratingsById) return null;
  const id = String(purchase?.playerId ?? purchase?.id ?? '');
  if (!id) return null;
  const holder = typeof ratingsById.get === 'function' ? ratingsById.get(id) : ratingsById[id];
  if (!holder) return null;

  const played = buildSeasonTrendSeries(holder).filter((r) => r.mark > 0);
  if (played.length < 3) return null; // troppo pochi dati per un trend affidabile

  const recent = played.slice(-RECENT_FORM_WINDOW);
  const recentAvg = recent.reduce((acc, r) => acc + r.fmark, 0) / recent.length;
  const seasonAvg = played.reduce((acc, r) => acc + r.fmark, 0) / played.length;
  return { recentAvg, seasonAvg, delta: recentAvg - seasonAvg, games: recent.length };
}

// Rischio disciplinare: tanti gialli in stagione aumentano il rischio di uno
// stop nelle prossime giornate. Niente conteggio "a quante giornate dalla
// squalifica": le regole sui blocchi/azzeramenti dei cartellini cambiano nel
// tempo e sbagliare quel numero darebbe un'informazione falsa — meglio un
// segnale prudente basato solo sul totale gialli.
export function getDisciplinaryRisk(purchase) {
  const ycards = v(purchase?.playerSnapshot?.ycards ?? purchase?.ycards);
  if (ycards >= 5) return { level: 'high', ycards };
  if (ycards >= 3) return { level: 'medium', ycards };
  return null;
}

// "Compra basso": un giocatore che l'anno scorso rendeva bene e quest'anno,
// con un numero di presenze già indicativo, sta rendendo molto meno del suo
// standard — spesso questi giocatori tornano sui loro livelli. Confronta solo
// dati stagionali reali (non calcPlayerScore, che mescola già più segnali),
// per restare una spiegazione semplice e verificabile.
function getBuyLowSignal(purchase) {
  const snap = purchase?.playerSnapshot || purchase || {};
  const prev = snap.previousSeasonStats || {};
  const caps = v(snap.caps);
  const prevCaps = v(prev.caps);
  if (caps < 3 || prevCaps < 8) return null; // troppo presto, o poco storico affidabile
  const prevFv = v(prev.fmarkavg);
  const curFv = v(snap.fmarkavg);
  if (prevFv <= 0) return null;
  const dropRatio = (prevFv - curFv) / prevFv;
  if (dropRatio > 0.15) return { prevFv, curFv, dropRatio };
  return null;
}

// Valuta la convenienza dello scambio dal punto di vista dell'ALTRA squadra.
// Due domande, entrambe necessarie: (1) quanto perde la sua formazione titolare
// cedendo quei giocatori? (2) cosa se ne fa dei giocatori che riceve? Con al
// massimo 11 titolari schierabili, ricevere doppioni che non vedranno mai il
// campo azzera la convenienza anche se i "numeri" dello scambio sono alla pari.
// Ritorna { acceptance: 1..10, whyThem: string[], whyNot: string[], theirStartersDelta, offeredUtility }.
function evaluateMutualBenefit({
  theirTeam,
  theirRoster,
  theirStartersBefore,
  theirStartersIds,
  offered, // giocatori che l'altra squadra RICEVE (i miei)
  received, // giocatori che l'altra squadra CEDE (i suoi)
  priceDelta, // combinedPrice ricevuto da me - prezzo del mio pacchetto
  starters,
  rolesMode,
  ratingsById, // opzionale: forma recente di chi riceve (vedi computeRecentFormDelta)
}) {
  const receivedIds = new Set(received.map((p) => String(p.playerId)));
  const theirAfter = [
    ...theirRoster.filter((p) => !receivedIds.has(String(p.playerId))),
    ...offered,
  ];
  const theirStartersDelta = topStartersScore(theirAfter, starters, rolesMode) - theirStartersBefore;
  const theirLossRatio = theirStartersDelta / Math.max(theirStartersBefore, 1);
  const startersGivenByThem = received.filter((p) => theirStartersIds.has(String(p.playerId))).length;

  const utility = assessOfferedPackageUtility({ offered, theirRoster, received, starters, rolesMode });

  const capsIn = sumCaps(offered);
  const capsOut = sumCaps(received);
  const fvIn = avgEffectiveFmarkavg(offered);
  const fvOut = avgEffectiveFmarkavg(received);

  let acceptance = 5;
  const whyThem = [];
  const whyNot = [];

  // (1) Impatto sui SUOI titolari quando cede i giocatori richiesti.
  if (theirStartersDelta > 0.01) {
    acceptance += 3;
    whyThem.push(`La formazione titolare di ${theirTeam} migliora perfino dopo lo scambio.`);
  } else if (theirLossRatio > -0.02) {
    acceptance += 2;
    if (startersGivenByThem === 0) {
      whyThem.push(`${theirTeam} cede solo giocatori di riserva: i suoi titolari restano praticamente invariati.`);
    } else {
      whyThem.push(`L'impatto sui titolari di ${theirTeam} è minimo: perde pochissimo dove conta.`);
    }
  } else if (theirLossRatio > -0.08) {
    acceptance -= 1;
    whyNot.push(`La sua formazione titolare perde qualcosa cedendo questi giocatori.`);
  } else {
    acceptance -= 2;
    if (startersGivenByThem > 0) {
      whyNot.push(`Sta cedendo ${startersGivenByThem === 1 ? 'un titolare' : `${startersGivenByThem} titolari`}: la sua formazione peggiora sensibilmente.`);
    } else {
      whyNot.push(`La sua formazione titolare peggiora sensibilmente dopo lo scambio.`);
    }
  }

  // (2) Utilità concreta dei giocatori che riceve.
  if (utility.starterArrivals.length > 0) {
    const names = utility.starterArrivals.map(getPurchaseName).join(', ');
    whyThem.unshift(
      utility.starterArrivals.length === 1
        ? `${names} entrerebbe subito nei titolari di ${theirTeam}.`
        : `${names} entrerebbero subito nei titolari di ${theirTeam}.`
    );
    if (theirStartersDelta <= 0.01) acceptance += 1;
  } else if (utility.usefulCount === 0) {
    // Nessun giocatore offerto troverebbe spazio: il classico "cosa me ne faccio?"
    acceptance -= 3;
    whyNot.push(`Cosa se ne fa? Nessuno dei giocatori offerti entra nei titolari di ${theirTeam} e in panchina ha già alternative di livello pari o superiore.`);
  } else {
    if (utility.depthFills.length > 0) {
      acceptance += 1;
      const names = utility.depthFills.map(getPurchaseName).join(', ');
      whyThem.push(`${names} copre un ruolo dove ${theirTeam} è senza riserve: profondità che gli serve davvero.`);
    }
    if (utility.benchUpgrades.length > 0) {
      acceptance += 1;
      const names = utility.benchUpgrades.map(getPurchaseName).join(', ');
      whyThem.push(`${names} alza il livello della sua panchina: copertura utile per turnover e infortuni.`);
    }
  }
  if (utility.useless.length > 0 && utility.usefulCount > 0) {
    acceptance -= 1;
    const names = utility.useless.map(getPurchaseName).join(', ');
    whyNot.push(`${names} non gli servirebbe: ha già alternative migliori in quel ruolo.`);
  }

  // Cedere riserve è un punto a favore solo se in cambio arriva qualcosa di utile.
  if (startersGivenByThem === 0 && theirStartersDelta <= 0.01 && utility.usefulCount > 0) {
    acceptance += 1;
  }

  if (priceDelta < 0) {
    acceptance += 1;
    whyThem.push(`Incassa un pacchetto pagato di più all'asta (+${-priceDelta} crediti di quota).`);
  } else if (priceDelta > 5) {
    acceptance -= 1;
    whyNot.push(`Cede giocatori pagati di più all'asta (${priceDelta} crediti di differenza).`);
  }

  if (capsIn > capsOut + 3) {
    acceptance += 1;
    whyThem.push(`Riceve giocatori più impiegati in stagione (${capsIn} presenze contro ${capsOut}).`);
  }

  if (fvIn > fvOut + 0.1) {
    acceptance += 1;
    whyThem.push(`La fantamedia media in arrivo è più alta (${fvIn.toFixed(2)} contro ${fvOut.toFixed(2)}).`);
  }

  // Produzione pesata per ruolo: chi riceve giocatori con un contributo al gol
  // superiore a quello che cede (tenendo conto che un attaccante "deve" segnare
  // di più di un difensore per essere altrettanto forte) ha un motivo in più
  // per accettare.
  const productionIn = sumRoleAwareProduction(offered, rolesMode);
  const productionOut = sumRoleAwareProduction(received, rolesMode);
  if (productionIn > productionOut * 1.1 && productionIn > productionOut + 5) {
    acceptance += 1;
    whyThem.push(`Il rendimento offensivo pesato per ruolo in arrivo è superiore a quello che cede.`);
  } else if (productionOut > productionIn * 1.1 && productionOut > productionIn + 5) {
    acceptance -= 1;
    whyNot.push(`Cede più rendimento offensivo (pesato per ruolo) di quanto riceve.`);
  }

  // Calendario: un calendario più morbido nelle prossime 7 giornate per chi
  // arriva è un punto a favore reale (giocheranno partite più abbordabili).
  const calendarIn = avgCalendarDifficulty(offered);
  const calendarOut = avgCalendarDifficulty(received);
  if (calendarIn !== null && calendarOut !== null && calendarOut - calendarIn > 0.4) {
    acceptance += 1;
    whyThem.push(`Chi arriva ha un calendario più morbido nelle prossime giornate rispetto a chi cede.`);
  } else if (calendarIn !== null && calendarOut !== null && calendarIn - calendarOut > 0.4) {
    acceptance -= 1;
    whyNot.push(`Chi arriva ha un calendario più duro nelle prossime giornate rispetto a chi cede.`);
  }

  // Segnali "da fantallenatore esperto" sui giocatori che RICEVONO (offered):
  // occasione da compra-basso, rischio diffida, forma recente. Aggiunti in
  // coda (push, non unshift) così non scavalcano l'impatto sui titolari, che
  // resta il motivo principale.
  offered.forEach((p) => {
    const name = getPurchaseName(p);

    const buyLow = getBuyLowSignal(p);
    if (buyLow) {
      acceptance += 1;
      whyThem.push(`🔥 ${name} sta rendendo il ${Math.round(buyLow.dropRatio * 100)}% sotto la sua media della scorsa stagione: un classico "compra basso", spesso questi giocatori risalgono.`);
    }

    const risk = getDisciplinaryRisk(p);
    if (risk?.level === 'high') {
      acceptance -= 1;
      whyNot.push(`🟨 ${name} è diffidato (${risk.ycards} gialli in stagione): rischia di saltare le prossime giornate.`);
    }

    const form = computeRecentFormDelta(p, ratingsById);
    if (form && form.delta > 1) {
      acceptance += 1;
      whyThem.push(`📈 ${name} è in un ottimo momento: nelle ultime ${form.games} giornate ${form.recentAvg.toFixed(2)} di media contro il ${form.seasonAvg.toFixed(2)} stagionale.`);
    } else if (form && form.delta < -1) {
      acceptance -= 1;
      whyNot.push(`📉 ${name} è in calo: nelle ultime ${form.games} giornate solo ${form.recentAvg.toFixed(2)} di media (stagionale ${form.seasonAvg.toFixed(2)}).`);
    }
  });

  if (!whyThem.length && !whyNot.length) {
    whyThem.push('Scambio alla pari nei ruoli: nessun reparto resta scoperto per nessuna delle due squadre.');
  }

  return {
    acceptance: Math.min(10, Math.max(1, acceptance)),
    whyThem: whyThem.slice(0, 4),
    whyNot: whyNot.slice(0, 3),
    theirStartersDelta,
    offeredUtility: utility,
  };
}

function cartesianProduct(groups) {
  return groups.reduce(
    (acc, group) => acc.flatMap((partial) => group.map((item) => [...partial, ...item])),
    [[]]
  );
}

// Un pacchetto condivide un giocatore con un altro già scelto? Prima si
// deduplicava solo sul giocatore più forte del pacchetto (calcPlayerScore):
// due combo che differivano solo nel "contorno" (es. {Milinkovic, Rossi} e
// {Milinkovic, Bianchi}) venivano considerate già abbastanza diverse perché
// il pezzo pregiato coincideva. Risultato: i 3 scambi mostrati per la stessa
// squadra sembravano "sempre gli stessi giocatori, a volte ne cambia uno o
// due".
function comboSharesPlayer(playersA, playersB) {
  const idsB = new Set(playersB.map((p) => String(p.playerId)));
  return playersA.some((p) => idsB.has(String(p.playerId)));
}

// Quando c'è un ruolo target esplicito (selezione multi-ruolo: "voglio
// rinforzare il centrocampo"), il giocatore che conta per l'utente è quello
// nel ruolo target, non il contorno che paga lo scambio. L'ordinamento
// globale però premia sempre il guadagno più alto proprio in quel ruolo: se
// per quella squadra c'è un candidato chiaramente migliore degli altri nel
// ruolo target, TUTTE le combo con quel candidato finiscono in cima, e senza
// un criterio dedicato i 3 pacchetti mostrati ripetono sempre lui, con solo
// il contorno diverso — esattamente il problema segnalato.
function getBucketPlayers(players, bucket, rolesMode) {
  if (!bucket) return players;
  return players.filter((p) => getPurchaseBucket(p, rolesMode) === bucket);
}

// Seleziona fino a maxCount combo dalla lista (già ordinata per qualità/
// probabilità di accettazione), privilegiando in ordine: (1) un giocatore
// diverso nel ruolo target, se presente; (2) un pacchetto completamente
// diverso (nessun giocatore in comune); (3) in ultima istanza, completa
// comunque la lista pur di non perdere proposte valide (a quel punto qualche
// ripetizione è inevitabile — es. quella squadra ha un solo candidato
// realistico in quel ruolo).
function pickDiverseCombos(sortedCombos, maxCount, { targetBucket, rolesMode } = {}) {
  const picked = [];

  const tryPick = (keyFn) => {
    for (const combo of sortedCombos) {
      if (picked.length >= maxCount) break;
      if (picked.includes(combo)) continue;
      const overlapsExisting = picked.some((p) => comboSharesPlayer(keyFn(combo), keyFn(p)));
      if (overlapsExisting) continue;
      picked.push(combo);
    }
  };

  if (targetBucket) {
    tryPick((combo) => getBucketPlayers(combo.players, targetBucket, rolesMode));
  }
  tryPick((combo) => combo.players);

  if (picked.length < maxCount) {
    for (const combo of sortedCombos) {
      if (picked.length >= maxCount) break;
      if (picked.includes(combo)) continue;
      picked.push(combo);
    }
  }

  return picked;
}

export function buildGroupTradeSuggestions({
  selectedPurchases = [],
  targetBucket,
  teamNames = [],
  rosterByTeam = {},
  myTeamName = '',
  rolesMode = 'classic',
  auction = null,
  // Cosa vuole ottenere il fantallenatore dallo scambio. `coverageWeight` 0..1
  // bilancia "alzare la fantamedia dell'11" (0) e "coprire meglio i reparti /
  // non giocare in 10" (1). `softCalendar`/`continuity` sono preferenze
  // aggiuntive che premiano chi arriva con calendario più morbido / più
  // presenze. `targetBucket` (già presente) è il ruolo da rinforzare.
  objective = {},
  // Disponibilità dei giocatori: Map/obj playerId -> { injury?: {reason,
  // expectedReturn, severity}, lineup?: {status:'starter'|'bench', prob} }.
  // Un pacchetto che ti porta un infortunato o un panchinaro fisso viene
  // segnalato e retrocesso.
  availabilityById = null,
  // Forma recente per il segnale "in un momento"/"in calo" (vedi
  // computeRecentFormDelta): Map/obj playerId -> { ratingsHistory, ratingsMaxDay }.
  ratingsById = null,
}) {
  const coverageWeight = Math.min(Math.max(Number(objective?.coverageWeight ?? 0.4), 0), 1);
  const wantSoftCalendar = Boolean(objective?.softCalendar);
  const wantContinuity = Boolean(objective?.continuity);

  const otherTeams = teamNames.filter((t) => t !== myTeamName);
  const myGroupScore = sumScore(selectedPurchases);
  const myGroupPrice = sumCredits(selectedPurchases);
  const maxAcceptableScore = myGroupScore * (1 + GROUP_SUGGESTION_MAX_GAIN_RATIO);

  const starters = getStartersReference(auction, rolesMode);
  const myRoster = (rosterByTeam[myTeamName] || []).filter((p) => !p?.deleted);
  const selectedIds = new Set(selectedPurchases.map((p) => String(p.playerId)));
  const myStartersBefore = topStartersScore(myRoster, starters, rolesMode);
  const myCoverageBefore = computeCoverageScore(myRoster, auction, rolesMode);
  const selectedCaps = sumCaps(selectedPurchases);
  const selectedCalendar = avgCalendarDifficulty(selectedPurchases);

  // Quanti giocatori offro per ciascun ruolo: il pacchetto ricevuto deve avere
  // esattamente lo stesso numero di giocatori per ruolo.
  const neededCountByBucket = {};
  const myScoreByBucket = {};
  selectedPurchases.forEach((p) => {
    const bucket = getPurchaseBucket(p, rolesMode);
    neededCountByBucket[bucket] = (neededCountByBucket[bucket] || 0) + 1;
    myScoreByBucket[bucket] = (myScoreByBucket[bucket] || 0) + calcPlayerScore(p);
  });
  const requiredBuckets = Object.keys(neededCountByBucket);

  const teamGroups = [];

  otherTeams.forEach((team) => {
    const roster = (rosterByTeam[team] || []).filter((p) => !p?.deleted);
    const theirStartersBefore = topStartersScore(roster, starters, rolesMode);
    const theirStartersIds = startersIdSet(roster, starters, rolesMode);

    const isMultiBucket = requiredBuckets.length > 1;

    // Per ogni ruolo richiesto, prepara le combinazioni possibili (sottoinsiemi
    // della grandezza giusta) di quella squadra in quel ruolo.
    const optionsByBucket = [];
    for (const bucket of requiredBuckets) {
      const needed = neededCountByBucket[bucket];
      const bucketRoster = roster.filter((p) => getPurchaseBucket(p, rolesMode) === bucket);
      if (bucketRoster.length < needed) return; // questa squadra non può pareggiare i ruoli richiesti

      const isTarget = bucket === targetBucket;
      const myBucketScore = myScoreByBucket[bucket] || 0;

      // Tetto e (quando la selezione è multi-ruolo) pavimento del valore
      // ricevibile in questo ruolo: nel ruolo target il tetto è il guadagno
      // massimo consentito e il pavimento un guadagno minimo reale (altrimenti
      // "scegliere" quel ruolo non cambierebbe nulla); nei ruoli non-target,
      // quando c'è un ruolo target esplicito, il range si inverte in perdita
      // (min/max indebolimento): è il prezzo pagato per finanziare il rinforzo.
      let ceilingRatio;
      let floorRatio;
      if (isTarget) {
        // Multi-ruolo: il tetto di realismo vero è quello sul PACCHETTO
        // COMPLETO (maxAcceptableScore, più sotto) — un candidato molto più
        // forte di quello offerto resta legittimo finché lo finanzi cedendo
        // abbastanza negli altri ruoli. Un tetto anche qui, per singolo
        // reparto, lasciava a volte un solo candidato modesto dentro la
        // fascia stretta: i 3 pacchetti proponevano sempre lui, con solo il
        // contorno diverso. Mono-ruolo (nessun reparto da sacrificare): il
        // tetto per-reparto resta necessario, altrimenti sarebbe un regalo.
        ceilingRatio = isMultiBucket ? Infinity : GROUP_SUGGESTION_MAX_GAIN_RATIO;
        floorRatio = isMultiBucket ? GROUP_SUGGESTION_MULTI_TARGET_MIN_GAIN_RATIO : -0.08;
      } else {
        ceilingRatio = -GROUP_SUGGESTION_MULTI_SIDE_MIN_LOSS_RATIO;
        floorRatio = -GROUP_SUGGESTION_MULTI_SIDE_MAX_LOSS_RATIO;
      }
      const bucketCeiling = myBucketScore * (1 + ceilingRatio);
      const bucketFloor = myBucketScore * (1 + floorRatio);

      // Il pool va filtrato per valore massimo PRIMA di limitarlo a POOL_SIZE:
      // altrimenti, se la squadra ha diversi giocatori più forti dei miei in
      // quel ruolo, i candidati realistici (alla pari o più deboli, quelli che
      // servono nei ruoli non target) finiscono fuori dal pool solo perché non
      // sono tra i più forti in assoluto della rosa avversaria in quel ruolo.
      // Il pavimento non si applica qui (solo necessario, non sufficiente,
      // quando needed > 1): si applica sulla SOMMA del pacchetto più sotto.
      const pool = bucketRoster
        .filter((p) => calcPlayerScore(p) <= bucketCeiling)
        .sort((a, b) => calcPlayerScore(b) - calcPlayerScore(a))
        .slice(0, GROUP_SUGGESTION_POOL_SIZE);

      if (pool.length < needed) return; // nessuna combinazione realistica possibile in questo ruolo

      const options = combinations(pool, needed)
        .filter((combo) => {
          const sum = sumScore(combo);
          return sum <= bucketCeiling && sum >= bucketFloor;
        })
        .sort((a, b) => sumScore(b) - sumScore(a))
        .slice(0, GROUP_SUGGESTION_MAX_OPTIONS_PER_BUCKET);

      if (!options.length) return; // nessuna opzione realistica in questo ruolo

      optionsByBucket.push({ bucket, options });
    }

    if (!optionsByBucket.length) return;

    const fullCombos = cartesianProduct(optionsByBucket.map((o) => o.options));

    const teamCombos = [];
    fullCombos.forEach((players) => {
      const combinedScore = sumScore(players);
      const combinedPrice = sumCredits(players);
      const scoreDelta = combinedScore - myGroupScore;
      const priceDelta = combinedPrice - myGroupPrice;

      // Propongo scambi in cui guadagno o resto sostanzialmente alla pari
      // (entro l'8%: possono comunque migliorare i miei titolari)...
      if (scoreDelta <= -myGroupScore * 0.08) return;
      // ...ma senza sproporzioni che l'altro manager rifiuterebbe a priori.
      if (combinedScore > maxAcceptableScore) return;

      // Il range di ciascun ruolo è già stato imposto a monte (pool + opzioni
      // per bucket): qui si ricalcola solo il dettaglio per ruolo, esposto al
      // chiamante per mostrare in UI il trade-off ("+2.1 in C, -0.6 in D").
      let preferredGain = 0;
      const bucketDeltas = requiredBuckets.map((bucket) => {
        const givenBucketScore = myScoreByBucket[bucket] || 0;
        const receivedBucketScore = players
          .filter((p) => getPurchaseBucket(p, rolesMode) === bucket)
          .reduce((acc, p) => acc + calcPlayerScore(p), 0);
        const bucketDelta = receivedBucketScore - givenBucketScore;
        if (bucket === targetBucket) preferredGain = bucketDelta;
        return { bucket, delta: bucketDelta };
      });

      // Convenienza per l'altra squadra: non è più un filtro rigido (svuotava
      // troppo spesso i risultati), ma alimenta ordinamento e badge; il
      // fallback più sotto preferisce comunque le proposte win-win quando ci sono.
      const mutual = evaluateMutualBenefit({
        theirTeam: team,
        theirRoster: roster,
        theirStartersBefore,
        theirStartersIds,
        offered: selectedPurchases,
        received: players,
        priceDelta,
        starters,
        rolesMode,
        ratingsById,
      });

      // Impatto sui MIEI titolari (per spiegare cosa ci guadagno davvero).
      const myAfter = [
        ...myRoster.filter((p) => !selectedIds.has(String(p.playerId))),
        ...players,
      ];
      const myStartersDelta = topStartersScore(myAfter, starters, rolesMode) - myStartersBefore;

      // Impatto sulla COPERTURA dei reparti (obiettivo "non giocare in 10").
      const myCoverageAfter = computeCoverageScore(myAfter, auction, rolesMode);
      const coverageDelta = myCoverageAfter.score - myCoverageBefore.score;
      const hardGapDelta = myCoverageAfter.hardGap - myCoverageBefore.hardGap;

      // Preferenze aggiuntive: calendario / continuità dei giocatori in arrivo.
      const comboCalendar = avgCalendarDifficulty(players);
      const calendarImprovement = (selectedCalendar !== null && comboCalendar !== null)
        ? selectedCalendar - comboCalendar
        : 0;
      const capsImprovement = sumCaps(players) - selectedCaps;

      // Punteggio-obiettivo: quanto questa proposta serve a ciò che l'utente
      // ha chiesto. `myStartersDelta` normalizzato sul valore del pacchetto,
      // `coverageDelta` su una scala fissa (~12 = un titolare coperto in più).
      const fantamediaComponent = myStartersDelta / Math.max(myGroupScore, 1);
      const coverageComponent = coverageDelta / 12
        - hardGapDelta * 1.5; // riaprire un buco pesa moltissimo
      const targetComponent = targetBucket
        ? preferredGain / Math.max(myScoreByBucket[targetBucket] || 1, 1)
        : 0;

      // Disponibilità dei giocatori in arrivo: un infortunato o un panchinaro
      // fisso abbassa parecchio l'utilità reale del pacchetto.
      const availability = assessListAvailability(players, availabilityById);

      const objectiveScore =
        (1 - coverageWeight) * fantamediaComponent +
        coverageWeight * coverageComponent +
        1.1 * targetComponent +
        (wantSoftCalendar ? Math.max(0, calendarImprovement) * 0.35 : 0) +
        (wantContinuity ? Math.max(0, capsImprovement) * 0.02 : 0) +
        (wantSoftCalendar && calendarImprovement < -0.3 ? calendarImprovement * 0.2 : 0)
        - availability.penalty;

      teamCombos.push({
        team,
        players,
        combinedScore,
        combinedPrice,
        scoreDelta,
        priceDelta,
        preferredGain,
        bucketDeltas,
        comboSize: players.length,
        acceptance: mutual.acceptance,
        whyThem: mutual.whyThem,
        whyNot: mutual.whyNot,
        myStartersDelta,
        coverageDelta,
        hardGapDelta,
        calendarImprovement,
        capsImprovement,
        availabilityFlags: availability.flags,
        objectiveScore,
      });
    });

    if (!teamCombos.length) return;

    teamCombos.sort((a, b) => {
      // Un pacchetto che ti porta un infortunato/panchinaro scivola sotto a
      // parità di fascia (a meno che non ce ne siano solo di quelli).
      const injA = (a.availabilityFlags || []).some((f) => f.kind === 'injury') ? 1 : 0;
      const injB = (b.availabilityFlags || []).some((f) => f.kind === 'injury') ? 1 : 0;
      if (injA !== injB) return injA - injB;
      // Prima le proposte con più probabilità di essere accettate (a fasce,
      // per non far dominare differenze minime)...
      const bandA = a.acceptance >= 7 ? 2 : a.acceptance >= 5 ? 1 : 0;
      const bandB = b.acceptance >= 7 ? 2 : b.acceptance >= 5 ? 1 : 0;
      if (bandA !== bandB) return bandB - bandA;
      // ...poi chi centra meglio l'obiettivo scelto dall'utente...
      const objDiff = b.objectiveScore - a.objectiveScore;
      if (Math.abs(objDiff) > 0.03) return objDiff;
      // ...a parità, il ruolo da rinforzare e infine il guadagno di valore.
      if (targetBucket) {
        const prefDiff = b.preferredGain - a.preferredGain;
        if (Math.abs(prefDiff) > 0.5) return prefDiff;
      }
      return b.scoreDelta - a.scoreDelta;
    });

    // Preferisci le proposte win-win; se per questa squadra non ce ne sono,
    // mostra comunque le migliori disponibili (il badge le segnala "difficili").
    const winWin = teamCombos.filter((c) => c.acceptance >= 4);
    const combos = pickDiverseCombos(winWin.length ? winWin : teamCombos, GROUP_SUGGESTION_MAX_PER_TEAM, {
      targetBucket,
      rolesMode,
    });

    teamGroups.push({
      team,
      combos,
      bestScoreDelta: combos[0].scoreDelta,
      bestObjectiveScore: Math.max(...combos.map((c) => c.objectiveScore)),
      bestAcceptance: Math.max(...combos.map((c) => c.acceptance)),
    });
  });

  teamGroups.sort((a, b) =>
    (b.bestAcceptance - a.bestAcceptance)
    || (b.bestObjectiveScore - a.bestObjectiveScore)
    || (b.bestScoreDelta - a.bestScoreDelta));

  return {
    myGroupScore,
    myGroupPrice,
    targetBucket,
    requiredBuckets,
    objective: { coverageWeight, softCalendar: wantSoftCalendar, continuity: wantContinuity },
    myCoverageBefore,
    teamGroups: teamGroups.slice(0, GROUP_SUGGESTION_MAX_TEAMS),
  };
}

// ── Shortlist: a quali squadre conviene di più proporre lo scambio ─────────
// Versione leggera di buildGroupTradeSuggestions: non costruisce i pacchetti,
// dà solo un ranking delle squadre in base a (1) quanto sono CORTE nei ruoli
// che stai offrendo e (2) se hanno riserve "cedibili" nella fascia di valore
// del tuo pacchetto da darti in cambio. Serve a non scegliere la squadra alla
// cieca. Ritorna al massimo `limit` voci con tier best/good/ok.
export function rankTargetTeams({
  selectedPurchases = [],
  teamNames = [],
  rosterByTeam = {},
  myTeamName = '',
  rolesMode = 'classic',
  auction = null,
  limit = 4,
}) {
  const otherTeams = teamNames.filter((t) => t !== myTeamName);
  if (!selectedPurchases.length || !otherTeams.length) return [];

  const starters = getStartersReference(auction, rolesMode);
  const myGroupScore = sumScore(selectedPurchases);
  const offeredBuckets = [...new Set(selectedPurchases.map((p) => getPurchaseBucket(p, rolesMode)))];

  // Lacune qualitative reali (non solo "quanti ne ho"), calcolate su TUTTE le
  // squadre della lega per avere una media di riferimento (vedi detectTeamWeaknesses).
  const teamStatsAll = buildTeamRosterStats({ teamNames, rosterByTeam, teamEconomy: {}, rolesMode, auction });
  const weaknessesByTeam = new Map(
    detectTeamWeaknesses(teamStatsAll).map((w) => [w.team, w.weaknesses])
  );

  const ranked = otherTeams.map((team) => {
    const roster = (rosterByTeam[team] || []).filter((p) => !p?.deleted);
    const cov = computeCoverageScore(roster, auction, rolesMode);
    const weaknesses = weaknessesByTeam.get(team) || [];

    const needBuckets = [];
    const weaknessReasons = [];
    let needScore = 0;
    let giveScore = 0;

    offeredBuckets.forEach((b) => {
      const inBucket = roster
        .filter((p) => getPurchaseBucket(p, rolesMode) === b)
        .map((p) => calcPlayerScore(p))
        .sort((x, y) => y - x);
      const needed = starters[b] || 0;

      // Bisogno: hanno meno di (titolari + 1 riserva) in quel ruolo?
      if (inBucket.length < needed + 1) {
        needScore += (needed + 1 - inBucket.length);
        needBuckets.push(b);
      }

      // Lacune qualitative reali su quel ruolo (rosa incompleta o rendimento
      // sotto la media di lega): pesano di più di un semplice conteggio, e
      // danno un motivo concreto da mostrare in UI.
      const roleWeakness = weaknesses.find((w) => w.role === b);
      if (roleWeakness) {
        needScore += WEAKNESS_SEVERITY[roleWeakness.severity] || 0;
        if (!needBuckets.includes(b)) needBuckets.push(b);
        const icon = roleWeakness.severity === 'high' ? '🚨' : '⚠️';
        weaknessReasons.push(`${icon} ${b}: ${roleWeakness.label.toLowerCase()} — ${roleWeakness.detail}`);
      }

      // Capacità di dare: riserve (oltre i titolari) nella fascia di valore del
      // mio pacchetto — quelle che potrebbero cedermi senza svenarsi.
      const bench = inBucket.slice(needed);
      giveScore += bench.filter((s) => s <= myGroupScore * 1.35 && s >= myGroupScore * 0.45).length;
    });

    const total = needScore * 2.2 + Math.min(giveScore, 4) + (cov.hardGap > 0 ? 2.5 : 0);
    return { team, total, needBuckets, giveScore, hardGap: cov.hardGap, weaknessReasons };
  });

  ranked.sort((a, b) => b.total - a.total);
  const shortlisted = ranked.filter((r) => r.total > 0.5).slice(0, limit);
  if (!shortlisted.length) return [];

  const bestTotal = shortlisted[0].total;
  return shortlisted.map((r, i) => ({
    team: r.team,
    tier: i === 0 ? 'best' : r.total >= bestTotal * 0.6 ? 'good' : 'ok',
    needBuckets: r.needBuckets,
    canGiveBack: r.giveScore > 0,
    hardGap: r.hardGap,
    weaknessReasons: r.weaknessReasons.slice(0, 3),
  }));
}

// ── Analisi di uno scambio specifico tra due gruppi di giocatori ────────────

export function analyzeTradeProposal({
  mySelected = [],
  theirSelected = [],
  myTeamName = '',
  theirTeamName = '',
  rolesMode = 'classic',
  auction = null,
  myRosterCounts = null,
  theirRosterCounts = null,
}) {
  const starters = getStartersReference(auction, rolesMode);

  const myScoreOut = sumScore(mySelected);
  const theirScoreOut = sumScore(theirSelected);
  const myPriceOut = sumCredits(mySelected);
  const theirPriceOut = sumCredits(theirSelected);

  const netScoreForMe = theirScoreOut - myScoreOut;
  const netCreditsForMe = theirPriceOut - myPriceOut;

  const baseline = Math.max((myScoreOut + theirScoreOut) / 2, 1);
  const ratio = netScoreForMe / baseline;

  let verdict = 'equilibrato';
  if (ratio > 0.12) verdict = 'me';
  else if (ratio < -0.12) verdict = 'loro';

  const givenByMe = countByBucket(mySelected, rolesMode);
  const givenByThem = countByBucket(theirSelected, rolesMode);

  const roleImpact = ROLE_ORDER.map((role) => {
    const myBefore = myRosterCounts?.[role] ?? null;
    const theirBefore = theirRosterCounts?.[role] ?? null;

    const myAfter = myBefore === null ? null : myBefore - (givenByMe[role] || 0) + (givenByThem[role] || 0);
    const theirAfter = theirBefore === null ? null : theirBefore - (givenByThem[role] || 0) + (givenByMe[role] || 0);

    return {
      role,
      myBefore,
      myAfter,
      theirBefore,
      theirAfter,
      myBelowStarters: myAfter !== null && myAfter < (starters[role] || 0),
      theirBelowStarters: theirAfter !== null && theirAfter < (starters[role] || 0),
    };
  });

  const warnings = [];
  if (mySelected.length !== theirSelected.length) {
    warnings.push(`Scambio sbilanciato nel numero di giocatori: ${mySelected.length} contro ${theirSelected.length}.`);
  }
  roleImpact.forEach((r) => {
    if (r.myBelowStarters) warnings.push(`Dopo lo scambio la tua rosa scenderebbe sotto i titolari necessari in ${r.role} (${r.myAfter}/${starters[r.role] || 0}).`);
    if (r.theirBelowStarters) warnings.push(`${theirTeamName || 'L\'altra squadra'} scenderebbe sotto i titolari necessari in ${r.role} (${r.theirAfter}/${starters[r.role] || 0}).`);
  });

  return {
    myTeamName,
    theirTeamName,
    myScoreOut,
    theirScoreOut,
    myPriceOut,
    theirPriceOut,
    netScoreForMe,
    netCreditsForMe,
    ratio,
    verdict,
    roleImpact,
    warnings,
  };
}

// ── Verdetto a due facce: conviene a ME o alla CONTROPARTE? ─────────────────
//
// analyzeTradeProposal dà un giudizio unico ("vantaggioso per te / equilibrato
// / sfavorevole") basato quasi solo sul valore. Qui invece si guarda lo scambio
// dai DUE lati separatamente e si argomenta: chi riceve più fantamedia? chi
// cede il pezzo più pregiato? chi guadagna in continuità d'impiego / calendario
// / rendimento? Se sono passate le rose (Win-To-Win) si aggiunge l'impatto su
// titolari e copertura reparti. Senza rose (Scambi Liberi) resta un confronto
// puro tra i due pacchetti — comunque motivato.
function sumValue(list = []) {
  return list.reduce((acc, p) => acc + v(p?.playerSnapshot?.value ?? p?.value ?? 0), 0);
}

function classifyStance(signalScore) {
  if (signalScore >= 1.5) return 'gain';
  if (signalScore <= -1.5) return 'loss';
  return 'fair';
}

export function assessTwoSidedTrade({
  mySelected = [],
  theirSelected = [],
  myTeamName = 'Tu',
  theirTeamName = 'La controparte',
  rolesMode = 'classic',
  auction = null,
  myRoster = null,
  theirRoster = null,
  availabilityById = null,
  // Forma recente per il segnale "in un momento"/"in calo" (vedi
  // computeRecentFormDelta): Map/obj playerId -> { ratingsHistory, ratingsMaxDay }.
  ratingsById = null,
}) {
  const starters = (Array.isArray(myRoster) || Array.isArray(theirRoster))
    ? getStartersReference(auction, rolesMode)
    : null;
  // "recv" = ciò che ricevo io (= ciò che cede la controparte)
  // "give" = ciò che offro io  (= ciò che riceve la controparte)
  const recvFmv = avgEffectiveFmarkavg(theirSelected);
  const giveFmv = avgEffectiveFmarkavg(mySelected);
  const recvCaps = sumCaps(theirSelected);
  const giveCaps = sumCaps(mySelected);
  const recvProd = sumRoleAwareProduction(theirSelected, rolesMode);
  const giveProd = sumRoleAwareProduction(mySelected, rolesMode);
  const recvVal = sumValue(theirSelected);
  const giveVal = sumValue(mySelected);
  const calRecv = avgCalendarDifficulty(theirSelected);
  const calGive = avgCalendarDifficulty(mySelected);
  const countDiff = theirSelected.length - mySelected.length;

  const fmt2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : '—');

  const forMe = { score: 0, reasons: [] };
  const forThem = { score: 0, reasons: [] };

  // (1) Fantamedia media dei pacchetti
  if (Math.abs(recvFmv - giveFmv) > 0.12) {
    if (recvFmv > giveFmv) {
      forMe.score += 1; forThem.score -= 1;
      forMe.reasons.push(`Ricevi più fantamedia: ${fmt2(recvFmv)} di media contro ${fmt2(giveFmv)} che cedi.`);
      forThem.reasons.push(`Cede la fantamedia più alta (${fmt2(giveFmv)} contro ${fmt2(recvFmv)}).`);
    } else {
      forThem.score += 1; forMe.score -= 1;
      forThem.reasons.push(`Riceve più fantamedia: ${fmt2(giveFmv)} contro ${fmt2(recvFmv)}.`);
      forMe.reasons.push(`Cedi la fantamedia più alta (${fmt2(giveFmv)} contro ${fmt2(recvFmv)}): occhio a non svalutare il pacchetto.`);
    }
  }

  // (2) Continuità d'impiego (presenze stagionali)
  if (Math.abs(recvCaps - giveCaps) >= 4) {
    if (recvCaps > giveCaps) {
      forMe.score += 0.8; forThem.score -= 0.4;
      forMe.reasons.push(`Chi arriva è più garantito: ${recvCaps} presenze stagionali contro ${giveCaps}.`);
    } else {
      forThem.score += 0.8; forMe.score -= 0.4;
      forMe.reasons.push(`Cedi i giocatori più continui (${giveCaps} presenze contro ${recvCaps}).`);
    }
  }

  // (3) Rendimento pesato per ruolo (un attaccante "deve" produrre più di un
  //     difensore per valere uguale)
  if (recvProd > giveProd * 1.12 && recvProd - giveProd > 4) {
    forMe.score += 1; forThem.score -= 0.6;
    forMe.reasons.push('Il rendimento pesato per ruolo dei giocatori in arrivo è più alto di quello che cedi.');
    forThem.reasons.push('Cede più rendimento (pesato per ruolo) di quanto riceve.');
  } else if (giveProd > recvProd * 1.12 && giveProd - recvProd > 4) {
    forThem.score += 1; forMe.score -= 0.6;
    forThem.reasons.push('Riceve più rendimento pesato per ruolo di quanto cede.');
    forMe.reasons.push('Cedi più rendimento offensivo/difensivo (pesato per ruolo) di quanto ricevi.');
  }

  // (4) "Pezzo pregiato": chi cede il giocatore di quotazione più alta
  const myTop = [...mySelected].sort((a, b) => calcPlayerScore(b) - calcPlayerScore(a))[0];
  const theirTop = [...theirSelected].sort((a, b) => calcPlayerScore(b) - calcPlayerScore(a))[0];
  if (myTop && theirTop) {
    const myTopScore = calcPlayerScore(myTop);
    const theirTopScore = calcPlayerScore(theirTop);
    if (myTopScore > theirTopScore * 1.15) {
      forThem.score += 1.2; forMe.score -= 1;
      forMe.reasons.push(`Nello scambio esce il tuo pezzo migliore, ${getPurchaseName(myTop)}: il singolo di maggior valore lo dai via tu.`);
      forThem.reasons.push(`Incassa il giocatore di maggior valore dello scambio (${getPurchaseName(myTop)}).`);
    } else if (theirTopScore > myTopScore * 1.15) {
      forMe.score += 1.2; forThem.score -= 1;
      forMe.reasons.push(`Ti porti a casa il pezzo pregiato dello scambio: ${getPurchaseName(theirTop)}.`);
      forThem.reasons.push(`Cede il giocatore di maggior valore (${getPurchaseName(theirTop)}).`);
    }
  }

  // (5) Valore di mercato complessivo del pacchetto
  if (Math.abs(recvVal - giveVal) >= 8) {
    if (recvVal > giveVal) {
      forMe.score += 0.6;
      forMe.reasons.push(`Il pacchetto in arrivo vale di più a listino (${Math.round(recvVal)} contro ${Math.round(giveVal)}).`);
    } else {
      forThem.score += 0.6;
      forThem.reasons.push(`Il pacchetto in arrivo per lui vale di più a listino (${Math.round(giveVal)} contro ${Math.round(recvVal)}).`);
    }
  }

  // (6) Calendario prossime giornate
  if (calRecv !== null && calGive !== null && Math.abs(calRecv - calGive) > 0.4) {
    if (calRecv < calGive) {
      forMe.score += 0.5; forThem.score -= 0.3;
      forMe.reasons.push('I giocatori in arrivo hanno un calendario più morbido nelle prossime giornate.');
    } else {
      forThem.score += 0.5; forMe.score -= 0.3;
      forMe.reasons.push('I giocatori in arrivo hanno un calendario più duro nelle prossime giornate.');
    }
  }

  // (7) Numero di giocatori
  if (countDiff !== 0) {
    if (countDiff > 0) {
      forMe.score += 0.4 * countDiff;
      forMe.reasons.push(`Ricevi ${countDiff} giocatore${countDiff > 1 ? 'i' : ''} in più di quanti ne cedi: più corpi in rosa.`);
      forThem.reasons.push(`Riduce la sua rosa di ${countDiff} giocatore${countDiff > 1 ? 'i' : ''}.`);
    } else {
      forThem.score += 0.4 * -countDiff;
      forMe.reasons.push(`Cedi ${-countDiff} giocatore${-countDiff > 1 ? 'i' : ''} in più di quanti ne ricevi: la tua rosa si accorcia.`);
    }
  }

  // (8) Contesto rosa (solo Win-To-Win): impatto su titolari e copertura
  if (Array.isArray(myRoster) && starters) {
    const myIds = new Set(mySelected.map((p) => String(p.playerId)));
    const myAfter = [
      ...myRoster.filter((p) => !p?.deleted && !myIds.has(String(p.playerId))),
      ...theirSelected,
    ];
    const covBefore = computeCoverageScore(myRoster, auction, rolesMode);
    const covAfter = computeCoverageScore(myAfter, auction, rolesMode);
    const startersBefore = topStartersScore(myRoster.filter((p) => !p?.deleted), starters, rolesMode);
    const startersAfter = topStartersScore(myAfter, starters, rolesMode);
    const sd = startersAfter - startersBefore;

    if (covAfter.hardGap < covBefore.hardGap) {
      forMe.score += 1.5;
      forMe.reasons.unshift('Con questo scambio smetti di rischiare di scendere in campo in 10: copri un reparto che era scoperto.');
    } else if (covAfter.hardGap > covBefore.hardGap) {
      forMe.score -= 2;
      forMe.reasons.unshift('Attenzione: dopo lo scambio un reparto resterebbe sotto i titolari necessari (rischi di giocare in 10).');
    } else if (covAfter.score > covBefore.score + 3) {
      forMe.score += 0.7;
      forMe.reasons.push('Migliora la copertura dei reparti: più alternative vere in panchina.');
    } else if (covAfter.score < covBefore.score - 3) {
      forMe.score -= 0.6;
      forMe.reasons.push('Perdi un po\' di copertura: qualche reparto resta più corto di riserve.');
    }

    if (sd > 0.01) {
      forMe.score += 1;
      forMe.reasons.push('La tua formazione titolare migliora di livello dopo lo scambio.');
    } else if (sd < -startersBefore * 0.03) {
      forMe.score -= 1;
      forMe.reasons.push('La tua formazione titolare perde qualcosa: stai cedendo dove conta.');
    }
  }

  if (Array.isArray(theirRoster) && starters) {
    const mutual = evaluateMutualBenefit({
      theirTeam: theirTeamName,
      theirRoster: theirRoster.filter((p) => !p?.deleted),
      theirStartersBefore: topStartersScore(theirRoster.filter((p) => !p?.deleted), starters, rolesMode),
      theirStartersIds: startersIdSet(theirRoster.filter((p) => !p?.deleted), starters, rolesMode),
      offered: mySelected,
      received: theirSelected,
      priceDelta: giveVal - recvVal,
      starters,
      rolesMode,
      ratingsById,
    });
    // acceptance 1..10 → contributo allo score "forThem" (5 = neutro)
    forThem.score += (mutual.acceptance - 5) / 2.5;
    mutual.whyThem.forEach((r) => { if (!forThem.reasons.includes(r)) forThem.reasons.push(r); });
    mutual.whyNot.forEach((r) => { if (!forThem.reasons.includes(r)) forThem.reasons.push(r); });
  }

  // (8b) Segnali "da fantallenatore esperto" su chi ARRIVO IO (theirSelected):
  // stessa lettura (compra-basso/diffida/forma recente) usata sopra per la
  // controparte, applicata qui dal mio punto di vista.
  theirSelected.forEach((p) => {
    const name = getPurchaseName(p);

    const buyLow = getBuyLowSignal(p);
    if (buyLow) {
      forMe.score += 1;
      forMe.reasons.push(`🔥 ${name} sta rendendo il ${Math.round(buyLow.dropRatio * 100)}% sotto la sua media della scorsa stagione: un classico "compra basso".`);
    }

    const risk = getDisciplinaryRisk(p);
    if (risk?.level === 'high') {
      forMe.score -= 1;
      forMe.reasons.push(`🟨 ${name} è diffidato (${risk.ycards} gialli): rischia di saltare le prossime giornate.`);
    }

    const form = computeRecentFormDelta(p, ratingsById);
    if (form && form.delta > 1) {
      forMe.score += 1;
      forMe.reasons.push(`📈 ${name} è in un ottimo momento: nelle ultime ${form.games} giornate ${form.recentAvg.toFixed(2)} di media contro il ${form.seasonAvg.toFixed(2)} stagionale.`);
    } else if (form && form.delta < -1) {
      forMe.score -= 1;
      forMe.reasons.push(`📉 ${name} è in calo: nelle ultime ${form.games} giornate solo ${form.recentAvg.toFixed(2)} di media.`);
    }
  });

  // (9) Disponibilità: infortuni e probabili formazioni sui giocatori scambiati.
  if (availabilityById) {
    const recvAvail = assessListAvailability(theirSelected, availabilityById);
    const giveAvail = assessListAvailability(mySelected, availabilityById);
    recvAvail.flags.forEach((f) => {
      if (f.kind === 'injury') {
        forMe.score -= 2; forThem.score += 0.8;
        forMe.reasons.unshift(`${f.name}, che riceveresti, è infortunato (${f.detail}).`);
        forThem.reasons.push(`Si libera di ${f.name}, attualmente infortunato.`);
      } else {
        forMe.score -= 0.7;
        forMe.reasons.push(`${f.name}, in arrivo, è dato in panchina nelle probabili formazioni.`);
      }
    });
    giveAvail.flags.forEach((f) => {
      if (f.kind === 'injury') {
        forMe.score += 1.5; forThem.score -= 1.5;
        forMe.reasons.push(`Ti liberi di ${f.name}, attualmente infortunato (${f.detail}).`);
        forThem.reasons.unshift(`${f.name}, che riceverebbe, è infortunato (${f.detail}).`);
      } else {
        forMe.score += 0.5;
        forMe.reasons.push(`Cedi ${f.name}, dato in panchina nelle probabili formazioni.`);
      }
    });
  }

  const meStance = classifyStance(forMe.score);
  const themStance = classifyStance(forThem.score);

  let headline;
  const diff = forMe.score - forThem.score;
  if (Math.abs(diff) < 1) headline = 'Scambio sostanzialmente equo per entrambi.';
  else if (diff > 0) headline = `Nel complesso conviene di più a te (${myTeamName}).`;
  else headline = `Nel complesso conviene di più a ${theirTeamName}.`;

  if (!forMe.reasons.length) forMe.reasons.push('Nessuno squilibrio evidente: pacchetti di livello simile.');
  if (!forThem.reasons.length) forThem.reasons.push('Nessuno squilibrio evidente dal suo punto di vista.');

  return {
    headline,
    forMe: { stance: meStance, reasons: forMe.reasons.slice(0, 4), score: forMe.score },
    forThem: { stance: themStance, reasons: forThem.reasons.slice(0, 4), score: forThem.score },
    myTeamName,
    theirTeamName,
  };
}

// ── Dati di dettaglio per il report di scambio (tabella + grafico + insight) ─

export function buildComparisonRows(purchases = []) {
  return purchases.map((p) => {
    const snap = p?.playerSnapshot || {};
    return {
      id: p.playerId,
      name: snap.name || p.playerName || '—',
      team: snap.team || p.playerTeam || '—',
      role: snap.role || p.role || '—',
      price: v(p.credits),
      value: v(snap.value),
      caps: v(snap.caps),
      markavg: v(snap.markavg),
      fmarkavg: v(snap.fmarkavg),
      goals: v(snap.goals),
      assists: v(snap.assists),
      ycards: v(snap.ycards),
      rcards: v(snap.rcards),
    };
  });
}

function averageForDay(seriesList, dayIndex) {
  const values = seriesList
    .map((s) => s[dayIndex])
    .filter((entry) => entry && entry.mark > 0)
    .map((entry) => entry.fmark);

  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

// mySidePlayers/theirSidePlayers: player doc completi (con ratingsHistory), es. da getPlayerById.
export function buildGroupTrendSeries({ mySidePlayers = [], theirSidePlayers = [] }) {
  const mySeries = mySidePlayers.map((p) => buildSeasonTrendSeries(p));
  const theirSeries = theirSidePlayers.map((p) => buildSeasonTrendSeries(p));
  const maxDay = Math.max(0, ...mySeries.map((s) => s.length), ...theirSeries.map((s) => s.length));

  const rows = [];
  for (let i = 0; i < maxDay; i++) {
    rows.push({
      day: i + 1,
      mine: averageForDay(mySeries, i),
      theirs: averageForDay(theirSeries, i),
    });
  }
  return rows;
}

// ── "Prova del nove": backtest sui fantavoti reali già giocati ──────────────
//
// A differenza di tutto il resto del motore (che stima PRIMA se lo scambio
// conviene), qui si guarda a POSTERIORI: nelle ultime N giornate realmente
// giocate, i giocatori in arrivo hanno totalizzato più fantapunti reali di
// quelli che cederesti? È un fatto, non una previsione — non tiene conto di
// formazione/turnover, serve come controprova concreta del verdetto.

function seriesFromRatingHolder(holder) {
  // holder: player doc completo (getPlayerById) OPPURE { ratingsHistory, ratingsMaxDay }
  if (!holder) return [];
  return buildSeasonTrendSeries(holder);
}

function sumSideOverWindow(seriesList, fromDay, toDay) {
  let total = 0;
  let games = 0;
  const perDay = [];
  for (let d = fromDay; d <= toDay; d += 1) {
    let dayFv = 0;
    let dayGames = 0;
    seriesList.forEach((s) => {
      const row = s[d - 1];
      if (row && row.mark > 0) {
        dayFv += row.fmark;
        dayGames += 1;
      }
    });
    total += dayFv;
    games += dayGames;
    perDay.push({ day: d, fv: Math.round(dayFv * 10) / 10, games: dayGames });
  }
  return { total: Math.round(total * 10) / 10, games, perDay };
}

// Confronto grezzo tra i due PACCHETTI (solo i giocatori scambiati), valido
// anche per gli Scambi Liberi (nessuna rosa).
// givePlayers / receivePlayers: player doc con ratingsHistory.
export function buildTradeBacktest({ givePlayers = [], receivePlayers = [], days = 5 }) {
  const giveSeries = givePlayers.map(seriesFromRatingHolder);
  const recvSeries = receivePlayers.map(seriesFromRatingHolder);
  const maxDay = Math.max(0, ...giveSeries.map((s) => s.length), ...recvSeries.map((s) => s.length));
  if (maxDay === 0) return null;

  const fromDay = Math.max(1, maxDay - days + 1);
  const give = sumSideOverWindow(giveSeries, fromDay, maxDay);
  const receive = sumSideOverWindow(recvSeries, fromDay, maxDay);

  return {
    days: maxDay - fromDay + 1,
    fromDay,
    toDay: maxDay,
    give,
    receive,
    delta: Math.round((receive.total - give.total) * 10) / 10,
  };
}

// Simulazione sull'11 titolare (solo Win-To-Win, serve la rosa completa):
// per ogni giornata della finestra sceglie il miglior XI del giorno (top-N per
// ruolo tra chi ha giocato) CON e SENZA lo scambio, e somma le differenze.
// seriesById: Map(playerId -> [{day, mark, fmark}...])  (già costruita dal chiamante)
const XI_MISSING_SLOT_FV = 5.5;

function bestXiFvForDay(roster, seriesById, starters, rolesMode, dayIndex) {
  const byBucket = { P: [], D: [], C: [], A: [] };
  roster.forEach((p) => {
    const bucket = getPurchaseBucket(p, rolesMode);
    if (!byBucket[bucket]) return;
    const s = seriesById.get(String(p.playerId));
    const row = s ? s[dayIndex] : null;
    if (row && row.mark > 0) byBucket[bucket].push(row.fmark);
  });
  return ROLE_ORDER.reduce((acc, role) => {
    const needed = starters[role] || 0;
    const played = byBucket[role].sort((a, b) => b - a);
    let roleFv = 0;
    for (let i = 0; i < needed; i += 1) {
      roleFv += i < played.length ? played[i] : XI_MISSING_SLOT_FV;
    }
    return acc + roleFv;
  }, 0);
}

export function buildXiBacktest({
  myRoster = [],
  theirRoster = [],
  mySelected = [],
  theirSelected = [],
  ratingsById,
  auction = null,
  rolesMode = 'classic',
  days = 5,
}) {
  if (!ratingsById) return null;
  const starters = getStartersReference(auction, rolesMode);
  if (!starters) return null;
  // ratingsById: Map(playerId -> { ratingsHistory, ratingsMaxDay }) → serie per-giorno
  const seriesById = new Map();
  ratingsById.forEach((holder, id) => {
    seriesById.set(String(id), buildSeasonTrendSeries(holder));
  });
  let maxDay = 0;
  seriesById.forEach((s) => { if (s.length > maxDay) maxDay = s.length; });
  if (maxDay === 0) return null;

  const fromDay = Math.max(1, maxDay - days + 1);
  const myGiveIds = new Set(mySelected.map((p) => String(p.playerId)));
  const theirGiveIds = new Set(theirSelected.map((p) => String(p.playerId)));

  const myBefore = (myRoster || []).filter((p) => !p?.deleted);
  const myAfter = [...myBefore.filter((p) => !myGiveIds.has(String(p.playerId))), ...theirSelected];
  const theirBefore = (theirRoster || []).filter((p) => !p?.deleted);
  const theirAfter = [...theirBefore.filter((p) => !theirGiveIds.has(String(p.playerId))), ...mySelected];

  let myDelta = 0;
  let theirDelta = 0;
  const perDay = [];
  for (let d = fromDay; d <= maxDay; d += 1) {
    const i = d - 1;
    const myD = bestXiFvForDay(myAfter, seriesById, starters, rolesMode, i)
      - bestXiFvForDay(myBefore, seriesById, starters, rolesMode, i);
    const theirD = bestXiFvForDay(theirAfter, seriesById, starters, rolesMode, i)
      - bestXiFvForDay(theirBefore, seriesById, starters, rolesMode, i);
    myDelta += myD;
    theirDelta += theirD;
    perDay.push({ day: d, mine: Math.round(myD * 10) / 10, theirs: Math.round(theirD * 10) / 10 });
  }

  return {
    days: maxDay - fromDay + 1,
    fromDay,
    toDay: maxDay,
    myDelta: Math.round(myDelta * 10) / 10,
    theirDelta: Math.round(theirDelta * 10) / 10,
    perDay,
  };
}

export function buildTradeInsights({ analysis, myRows = [], theirRows = [] }) {
  const insights = [];
  const sum = (rows, key) => rows.reduce((acc, r) => acc + v(r[key]), 0);
  const avg = (rows, key) => (rows.length ? sum(rows, key) / rows.length : 0);

  const myAvgFmv = avg(myRows, 'fmarkavg');
  const theirAvgFmv = avg(theirRows, 'fmarkavg');
  if (Math.abs(myAvgFmv - theirAvgFmv) > 0.15) {
    insights.push(
      theirAvgFmv > myAvgFmv
        ? `I giocatori in arrivo hanno una fantamedia superiore (${theirAvgFmv.toFixed(2)} contro ${myAvgFmv.toFixed(2)}).`
        : `I giocatori che offri hanno una fantamedia superiore (${myAvgFmv.toFixed(2)} contro ${theirAvgFmv.toFixed(2)}): occhio a non svalutare il pacchetto.`
    );
  }

  const myCaps = sum(myRows, 'caps');
  const theirCaps = sum(theirRows, 'caps');
  if (Math.abs(myCaps - theirCaps) >= 3) {
    insights.push(
      theirCaps > myCaps
        ? `Il gruppo in arrivo totalizza più presenze stagionali (${theirCaps} contro ${myCaps}): maggiore continuità d'impiego.`
        : `Il tuo gruppo ha più presenze stagionali (${myCaps} contro ${theirCaps}): stai cedendo giocatori più impiegati.`
    );
  }

  const myProd = sum(myRows, 'goals') + sum(myRows, 'assists');
  const theirProd = sum(theirRows, 'goals') + sum(theirRows, 'assists');
  if (myProd !== theirProd) {
    insights.push(
      theirProd > myProd
        ? `Il pacchetto in arrivo porta più gol+assist complessivi (${theirProd} contro ${myProd}).`
        : `Stai cedendo più produzione offensiva complessiva (${myProd} gol+assist contro ${theirProd}).`
    );
  }

  const myCards = sum(myRows, 'ycards') + sum(myRows, 'rcards') * 2;
  const theirCards = sum(theirRows, 'ycards') + sum(theirRows, 'rcards') * 2;
  if (Math.abs(myCards - theirCards) >= 2) {
    insights.push(
      theirCards > myCards
        ? 'I giocatori in arrivo sono più indisciplinati (più cartellini): rischio squalifiche.'
        : 'Il tuo pacchetto è più indisciplinato dei giocatori in arrivo: meno rischio squalifiche dopo lo scambio.'
    );
  }

  // Calendario Serie A: difficoltà media delle prossime 7 giornate dei club
  // di appartenenza dei giocatori coinvolti (1 = morbido, 5 = durissimo).
  const avgCalendarDifficulty = (rows) => {
    const values = rows
      .map((r) => getUpcomingCalendarDifficulty(r.team, 7))
      .filter((d) => d !== null);
    return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
  };
  const myCalendar = avgCalendarDifficulty(myRows);
  const theirCalendar = avgCalendarDifficulty(theirRows);
  if (myCalendar !== null && theirCalendar !== null && Math.abs(myCalendar - theirCalendar) >= 0.35) {
    insights.push(
      theirCalendar < myCalendar
        ? `Calendario a favore: nelle prossime 7 giornate i giocatori in arrivo affrontano avversarie più morbide (difficoltà media ${theirCalendar.toFixed(1)} contro ${myCalendar.toFixed(1)}).`
        : `Occhio al calendario: nelle prossime 7 giornate i giocatori in arrivo hanno impegni più duri (difficoltà media ${theirCalendar.toFixed(1)} contro ${myCalendar.toFixed(1)}).`
    );
  }

  if (analysis) {
    insights.push(
      analysis.verdict === 'me'
        ? `Indice qualità complessivo a tuo favore di +${analysis.netScoreForMe.toFixed(1)} punti.`
        : analysis.verdict === 'loro'
          ? `Indice qualità complessivo sfavorevole di ${analysis.netScoreForMe.toFixed(1)} punti: valuta di chiedere un conguaglio.`
          : `Indice qualità complessivo sostanzialmente in pareggio (${analysis.netScoreForMe >= 0 ? '+' : ''}${analysis.netScoreForMe.toFixed(1)}).`
    );
  }

  return insights;
}
